import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import apiClient from "../lib/api";
import AdminSection from "../components/admin/AdminSection";
import { toast } from "sonner";
import {
  Trophy, Star, Lock, CheckCircle, Crown, ArrowLeft,
  UploadSimple, X, Sparkle, Clock, ShieldCheck, Warning,
  CurrencyDollar, ArrowClockwise, Info,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

function fmt(iso) {
  if (!iso) return "";
  try { return format(new Date(iso), "dd MMM yyyy", { locale: es }); }
  catch { return iso; }
}

/* ─── Info Tooltip ───────────────────────────────────────────────── */
function InfoTooltip({ pick }) {
  const [visible, setVisible] = useState(false);

  const MARKET_LABELS = {
    "1x2": "Resultado final (1X2)",
    "over_under": "Más/Menos goles",
    "btts": "Ambos equipos anotan",
    "asian_handicap": "Hándicap asiático",
    "double_chance": "Doble oportunidad",
    "moneyline": "Ganador directo",
    "spread": "Hándicap de puntos",
    "total_points": "Total de puntos",
  };

  const label = MARKET_LABELS[pick.market] || pick.market;

  return (
    <div className="relative inline-flex items-center" style={{ zIndex: 10 }}>
      <button
        type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="p-0.5 rounded-full text-zinc-400 hover:text-yellow-400 transition-colors focus:outline-none"
        aria-label="Más información sobre este pick"
        data-testid={`pick-info-tooltip-${pick.home_team}-${pick.away_team}`.replace(/\s/g, "_")}
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {visible && (
        <div
          className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-56 rounded-xl p-3 shadow-2xl pointer-events-none"
          style={{
            background: "#18181F",
            border: "1px solid rgba(212,175,55,0.2)",
            zIndex: 50,
          }}
        >
          {/* Arrow */}
          <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45"
            style={{ background: "#18181F", borderLeft: "1px solid rgba(212,175,55,0.2)", borderBottom: "1px solid rgba(212,175,55,0.2)" }} />

          <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-[0.2em] mb-1.5">Detalle del pick</p>

          <div className="space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-[10px] text-zinc-500 w-16 flex-shrink-0 pt-0.5">Mercado</span>
              <span className="text-xs text-white font-semibold">{label || pick.pick_selection}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[10px] text-zinc-500 w-16 flex-shrink-0 pt-0.5">Apuesta</span>
              <span className="text-xs text-zinc-200">{pick.pick_selection}</span>
            </div>
            {pick.league && (
              <div className="flex items-start gap-2">
                <span className="text-[10px] text-zinc-500 w-16 flex-shrink-0 pt-0.5">Liga</span>
                <span className="text-xs text-zinc-200">{pick.league}</span>
              </div>
            )}
            <div className="flex items-start gap-2">
              <span className="text-[10px] text-zinc-500 w-16 flex-shrink-0 pt-0.5">Cuota</span>
              <span className="text-xs font-black text-yellow-400">{pick.odds?.toFixed(2)}</span>
            </div>
            {pick.analysis && (
              <div className="pt-1 border-t border-white/[0.06]">
                <p className="text-[10px] text-zinc-400 italic leading-relaxed">{pick.analysis}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disclaimer de riesgo — letra pequeña */}
      <p className="text-[10px] text-zinc-400 leading-relaxed mt-10 max-w-2xl">
        Aviso: los parlays son apuestas de alto riesgo. Aunque el premio potencial es mayor,
        la probabilidad de acertar disminuye con cada selección agregada. Apuesta solo lo que
        estés dispuesto a perder. +18. Juega con responsabilidad.
      </p>
    </div>
  );
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function IQStars({ score }) {
  const filled = Math.round((score / 10) * 5);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} weight={i <= filled ? "fill" : "regular"}
          className={`w-3.5 h-3.5 ${i <= filled ? "text-yellow-400" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

const RESULT_BADGE = {
  won:  { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Ganado" },
  lost: { cls: "bg-red-500/15 text-red-400 border-red-500/30",            label: "Perdido" },
  void: { cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",          label: "Anulado" },
};

/* ─── Payment Modal ──────────────────────────────────────────────────────── */
function PurchaseModal({ parlay, onClose, onSuccess }) {
  const [step, setStep] = useState("method");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get("/payment-methods");
        setPaymentMethods(res.data.methods || []);
      } catch { /* silent */ }
      setLoadingMethods(false);
    };
    load();
  }, []);

  const methodData = paymentMethods.find(m => m.type === selectedMethod);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Archivo muy grande (máx 5MB)"); return; }
    const b64 = await toBase64(file);
    setProofFile(file);
    setProofPreview(b64);
  };

  const handleSubmit = async () => {
    if (!proofPreview) { toast.error("Adjunta el comprobante de pago"); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/parlays/${parlay.id}/purchase`, {
        payment_method: selectedMethod,
        payment_proof_image: proofPreview,
      });
      toast.success("Comprobante enviado — el admin revisará tu pago en breve");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    }
    setSubmitting(false);
  };

  const METHOD_LABEL = { zelle: "Zelle", pago_movil: "Pago Móvil", binance: "Binance" };
  const METHOD_COLOR = { zelle: "text-purple-400 border-purple-500/30", pago_movil: "text-sky-400 border-sky-500/30", binance: "text-yellow-400 border-yellow-500/30" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.12] w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            {step === "details" && (
              <button onClick={() => { setStep("method"); setProofFile(null); setProofPreview(null); }}
                className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-xs mb-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Volver
              </button>
            )}
            <h2 className="font-black text-lg">
              {step === "method" ? "Comprar Parlay" : `Pago con ${METHOD_LABEL[selectedMethod]}`}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {parlay.title} — <span className="text-yellow-400 font-bold">${parlay.price} USD</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "method" && (
          <div className="space-y-2">
            <p className="text-xs text-zinc-500 mb-3">Elige tu método de pago preferido:</p>
            {loadingMethods ? (
              <div className="text-center py-6 text-zinc-500 text-sm">Cargando métodos...</div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-6 text-zinc-500 text-sm">
                <Warning className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay métodos de pago disponibles.</p>
                <p className="text-xs mt-1">Contacta al administrador.</p>
              </div>
            ) : paymentMethods.map(m => (
              <button key={m.id} onClick={() => { setSelectedMethod(m.type); setStep("details"); }}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border bg-white/[0.03] hover:bg-white/[0.07] transition-all ${METHOD_COLOR[m.type] || "text-white border-white/[0.1]"}`}
                data-testid={`parlay-pay-method-${m.type}`}>
                <CurrencyDollar className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-bold text-sm">{METHOD_LABEL[m.type] || m.name}</div>
                  {m.account_info && <div className="text-xs opacity-70">{m.account_info}</div>}
                </div>
              </button>
            ))}
          </div>
        )}

        {step === "details" && methodData && (
          <div className="space-y-4">
            {/* Account info */}
            <div className="rounded-xl p-4 border border-white/[0.08] bg-white/[0.03]">
              <div className="text-xs text-zinc-500 mb-2 uppercase tracking-[0.15em]">Envía ${parlay.price} USD a:</div>
              <div className="font-black text-xl text-yellow-400">{methodData.account_info}</div>
              {methodData.account_name && <div className="text-sm text-zinc-400 mt-1">{methodData.account_name}</div>}
              {methodData.instructions && (
                <div className="mt-3 text-xs text-zinc-400 bg-white/[0.02] rounded-lg p-3 whitespace-pre-wrap">
                  {methodData.instructions}
                </div>
              )}
            </div>

            {/* Proof upload */}
            <div>
              <label className="block text-xs font-bold text-zinc-400 uppercase tracking-[0.15em] mb-2">
                Adjunta comprobante de pago
              </label>
              {proofPreview ? (
                <div className="relative rounded-xl overflow-hidden border border-white/[0.1]">
                  <img src={proofPreview} alt="Comprobante" className="w-full max-h-48 object-cover" />
                  <button onClick={() => { setProofFile(null); setProofPreview(null); }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-red-500/80">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label htmlFor="parlay-proof-upload"
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-white/[0.1] hover:border-yellow-400/40 cursor-pointer transition-colors"
                  data-testid="parlay-proof-upload-area">
                  <UploadSimple className="w-8 h-8 text-zinc-500" />
                  <div className="text-sm text-zinc-500 text-center">
                    <span className="text-yellow-400 font-semibold">Selecciona imagen</span>
                    <p className="text-xs mt-1">PNG, JPG, WEBP — máx 5MB</p>
                  </div>
                  <input id="parlay-proof-upload" type="file" accept="image/*" onChange={handleFileChange}
                    className="hidden" data-testid="parlay-proof-input" />
                </label>
              )}
            </div>

            <button onClick={handleSubmit} disabled={!proofPreview || submitting}
              className="btn-primary w-full" data-testid="parlay-purchase-submit">
              {submitting ? (
                <><ArrowClockwise className="w-4 h-4 animate-spin" /> Enviando...</>
              ) : (
                <><CheckCircle className="w-4 h-4" /> Enviar comprobante</>
              )}
            </button>

            <p className="text-xs text-zinc-400 text-center">
              El admin verificará tu pago. Recibirás acceso una vez aprobado.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Parlay Card (Public - before purchase) ─────────────────── */
function ParlayCard({ parlay, purchased, purchasedData, onBuy }) {
  const [expanded, setExpanded] = useState(false);
  const st = RESULT_BADGE[parlay.result];
  const isApproved = purchasedData?.purchase?.status === "approved";
  const isPending = purchasedData?.purchase?.status === "pending";

  return (
    <div className="rounded-2xl border overflow-hidden" style={{
      background: "linear-gradient(145deg, #16161A 0%, #111117 100%)",
      borderColor: purchased && isApproved ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.07)",
      boxShadow: purchased && isApproved ? "0 0 30px rgba(212,175,55,0.08)" : "0 4px 20px rgba(0,0,0,0.4)",
    }}
      data-testid={`parlay-card-${parlay.id}`}>

      {/* Top accent */}
      {purchased && isApproved && (
        <div style={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.8), transparent)" }} />
      )}

      <div className="p-5 lg:p-6">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {st && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
              )}
              {purchased && isApproved && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-yellow-400/10 text-yellow-400 border-yellow-400/30">
                  Comprado
                </span>
              )}
              {isPending && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-500/10 text-zinc-400 border-zinc-500/20 flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> Pago pendiente
                </span>
              )}
            </div>
            <h3 className="font-black text-lg leading-tight">{parlay.title}</h3>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-black text-yellow-400 leading-none">{parlay.total_odds?.toFixed(2)}x</div>
            <div className="text-xs text-zinc-500 mt-0.5">cuota total</div>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center gap-1.5">
            <IQStars score={parlay.iq_score || 8} />
            <span className="text-xs text-zinc-500">IQ {parlay.iq_score?.toFixed(1)}</span>
          </div>
          <div className="text-xs text-zinc-500">{(parlay.picks || []).length} picks</div>
          {parlay.max_sales && (
            <div className="text-xs text-zinc-500">
              {parlay.current_sales}/{parlay.max_sales} cupos
              {parlay.current_sales >= parlay.max_sales && <span className="text-red-400 ml-1">Agotado</span>}
            </div>
          )}
        </div>

        {/* Admin notes — only after approved purchase */}
        {purchased && isApproved && purchasedData?.parlay?.admin_notes && (
          <div className="rounded-xl p-4 mb-4 border" style={{
            background: "rgba(212,175,55,0.06)", borderColor: "rgba(212,175,55,0.2)",
          }}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck weight="fill" className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              <span className="text-xs font-bold text-yellow-400 uppercase tracking-[0.15em]">Indicaciones de Ronald</span>
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed">{purchasedData.parlay.admin_notes}</p>
          </div>
        )}

        {/* Picks list */}
        <div className="space-y-2 mb-5">
          {(parlay.picks || []).map((pick, i) => {
            const pickResult = RESULT_BADGE[pick.status];
            return (
              <div key={i} className="flex items-start gap-3 rounded-lg p-3 bg-white/[0.03] border border-white/[0.04]">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  pick.status === "won" ? "bg-emerald-500" : pick.status === "lost" ? "bg-red-500" : "bg-zinc-700"
                }`}>
                  {pick.status === "won" ? <CheckCircle className="w-3 h-3 text-white" weight="fill" /> :
                   pick.status === "lost" ? <X className="w-3 h-3 text-white" /> :
                   <span className="text-[8px] text-zinc-400 font-bold">{i + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-semibold truncate">{pick.home_team} vs {pick.away_team}</p>
                      <InfoTooltip pick={pick} />
                    </div>
                    <span className="text-sm font-black text-yellow-400 flex-shrink-0">{pick.odds?.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{pick.league} · {pick.pick_selection}</p>
                  {/* Analysis only for approved purchases */}
                  {purchased && isApproved && pick.analysis && (
                    <p className="text-xs text-zinc-400 mt-1 italic">{pick.analysis}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {!purchased && parlay.status === "active" && !(parlay.max_sales && parlay.current_sales >= parlay.max_sales) && (
          <button onClick={() => onBuy(parlay)}
            className="btn-primary w-full py-3.5 text-base font-black"
            data-testid={`parlay-buy-btn-${parlay.id}`}>
            <Crown weight="fill" className="w-5 h-5" />
            Comprar — ${parlay.price} USD
          </button>
        )}
        {parlay.max_sales && parlay.current_sales >= parlay.max_sales && !purchased && (
          <div className="w-full py-3.5 rounded-xl text-center text-sm font-bold text-zinc-400 bg-white/[0.02] border border-white/[0.05]">
            Agotado
          </div>
        )}
        {isPending && !isApproved && (
          <div className="w-full py-3 rounded-xl text-center text-sm font-bold text-yellow-400 bg-yellow-400/5 border border-yellow-400/20 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" /> Pago en revisión — en breve tendrás acceso
          </div>
        )}
        {purchased && isApproved && (
          <div className="w-full py-3 rounded-xl text-center text-sm font-bold text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-center gap-2">
            <CheckCircle weight="fill" className="w-4 h-4" /> Acceso completo desbloqueado
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function ParlaysPage() {
  const { user } = useAuth();
  const [parlays, setParlays] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyModal, setBuyModal] = useState(null);
  const [tab, setTab] = useState("available");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [publicRes, myRes] = await Promise.all([
        apiClient.get("/parlays"),
        apiClient.get("/parlays/my"),
      ]);
      setParlays(publicRes.data || []);
      setMyPurchases(myRes.data || []);
    } catch { toast.error("Error cargando parlays"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getPurchaseData = (parlayId) =>
    myPurchases.find(p => p.parlay?.id === parlayId || p.purchase?.parlay_id === parlayId);

  const isPurchased = (parlayId) => Boolean(getPurchaseData(parlayId));

  const approvedPurchases = myPurchases.filter(p => p.purchase?.status === "approved");
  const pendingPurchases = myPurchases.filter(p => p.purchase?.status === "pending");

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold">Premium</div>
          <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
            style={{ background: "rgba(239,68,68,0.12)", color: "#F87171", border: "1px solid rgba(239,68,68,0.3)" }}>
            Modo Experto
          </span>
        </div>
        <h1 className="text-4xl font-black tracking-tighter mb-2">Parlays</h1>
        <p className="text-zinc-400 text-sm max-w-xl">
          Combinaciones curadas por Ronald. Compra acceso completo con indicaciones exclusivas y análisis detallado de cada pick.
        </p>
        <p className="text-zinc-500 text-xs max-w-xl mt-2 leading-relaxed">
          Los parlays combinan varias jugadas en una sola apuesta: pagan más, pero con más riesgo —
          si una sola selección falla, se pierde todo. Recomendados para jugadores con experiencia.
        </p>
      </div>

      {/* Stats bar */}
      {myPurchases.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/20 rounded-xl px-4 py-2.5">
            <Trophy weight="fill" className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-bold text-yellow-400">{approvedPurchases.length} comprado{approvedPurchases.length !== 1 ? "s" : ""}</span>
          </div>
          {pendingPurchases.length > 0 && (
            <div className="flex items-center gap-2 bg-zinc-500/10 border border-zinc-500/20 rounded-xl px-4 py-2.5">
              <Clock className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-400">{pendingPurchases.length} en revisión</span>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-obsidian-800 border border-white/[0.06] rounded-md mb-6 w-fit">
        <button onClick={() => setTab("available")}
          className={`px-4 py-1.5 text-xs font-bold rounded transition-colors ${tab === "available" ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"}`}
          data-testid="parlays-tab-available">
          Disponibles ({parlays.length})
        </button>
        {approvedPurchases.length > 0 && (
          <button onClick={() => setTab("mine")}
            className={`px-4 py-1.5 text-xs font-bold rounded transition-colors flex items-center gap-1.5 ${tab === "mine" ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"}`}
            data-testid="parlays-tab-mine">
            Mis Compras ({approvedPurchases.length})
          </button>
        )}
      </div>

      <AdminSection
        icon={Trophy}
        title={tab === "available" ? "Parlays disponibles" : "Mis parlays"}
        subtitle={tab === "available" ? "Combinaciones premium listas para comprar" : "Tus accesos completos con análisis detallado"}
        color="amber"
      >
      {loading ? (
        <div className="grid gap-4">
          {[1, 2].map(i => (
            <div key={i} className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6 animate-pulse" style={{ minHeight: 220 }} />
          ))}
        </div>
      ) : tab === "available" ? (
        <div className="grid gap-6">
          {parlays.length === 0 ? (
            <div className="text-center py-20 text-zinc-400">
              <Trophy className="w-14 h-14 mx-auto mb-4 opacity-20" />
              <p className="font-semibold text-zinc-500">Próximamente</p>
              <p className="text-sm mt-1">Ronald está preparando los próximos parlays premium.</p>
            </div>
          ) : parlays.map(parlay => (
            <ParlayCard
              key={parlay.id}
              parlay={parlay}
              purchased={isPurchased(parlay.id)}
              purchasedData={getPurchaseData(parlay.id)}
              onBuy={setBuyModal}
            />
          ))}
        </div>
      ) : (
        <div className="grid gap-6">
          {approvedPurchases.map(({ purchase, parlay }) => parlay && (
            <ParlayCard
              key={parlay.id}
              parlay={parlay}
              purchased
              purchasedData={{ purchase, parlay }}
              onBuy={() => {}}
            />
          ))}
        </div>
      )}
      </AdminSection>

      {buyModal && (
        <PurchaseModal
          parlay={buyModal}
          onClose={() => setBuyModal(null)}
          onSuccess={() => { setBuyModal(null); load(); }}
        />
      )}
    </div>
  );
}
